import React, { useState } from "react";
import { Box, Typography, Paper, List, ListItem, ListItemButton, ListItemText, Avatar, Skeleton } from "@mui/material";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { HeroImageSettingsModal } from "../../components/admin/HeroImageSettingsModal";
import { AboutImageSettingsModal } from "../../components/admin/AboutImageSettingsModal";
import { AboutHeroImageSettingsModal } from "../../components/admin/AboutHeroImageSettingsModal";
import { ContactHeroImageSettingsModal } from "../../components/admin/ContactHeroImageSettingsModal";
import { usePublicSiteSettings } from "../../hooks/useSiteSettings";

interface ImageSetting {
  key: string;
  label: string;
  description: string;
}

const IMAGE_SETTINGS: ImageSetting[] = [
  {
    key: "hero_image_url",
    label: "Main Hero Image",
    description: "Homepage hero background",
  },
  {
    key: "about_hero_image_url",
    label: "About Hero Image",
    description: "About page hero background",
  },
  {
    key: "about_image_url",
    label: "About Page Image",
    description: "About page portrait image",
  },
  {
    key: "contact_hero_image_url",
    label: "Connect Hero Image",
    description: "Connect page hero background",
  },
];

export const ImageSettingsPage: React.FC = () => {
  const { data: siteSettings, isLoading } = usePublicSiteSettings();
  const [heroImageModalOpen, setHeroImageModalOpen] = useState(false);
  const [aboutImageModalOpen, setAboutImageModalOpen] = useState(false);
  const [aboutHeroImageModalOpen, setAboutHeroImageModalOpen] = useState(false);
  const [contactHeroImageModalOpen, setContactHeroImageModalOpen] = useState(false);

  const handleItemClick = (key: string) => {
    // Close all modals first
    setHeroImageModalOpen(false);
    setAboutImageModalOpen(false);
    setAboutHeroImageModalOpen(false);
    setContactHeroImageModalOpen(false);

    // Open the corresponding modal
    switch (key) {
      case "hero_image_url":
        setHeroImageModalOpen(true);
        break;
      case "about_hero_image_url":
        setAboutHeroImageModalOpen(true);
        break;
      case "about_image_url":
        setAboutImageModalOpen(true);
        break;
      case "contact_hero_image_url":
        setContactHeroImageModalOpen(true);
        break;
    }
  };

  const handleModalClose = () => {
    // Close all modals
    setHeroImageModalOpen(false);
    setAboutImageModalOpen(false);
    setAboutHeroImageModalOpen(false);
    setContactHeroImageModalOpen(false);
  };

  const getImageUrl = (key: string): string | undefined => {
    return siteSettings?.settings?.[key];
  };

  const getPreviewUrl = (imageUrl: string | undefined): string | undefined => {
    if (!imageUrl) {
      return undefined;
    }
    // If it's a Cloudinary URL, create a thumbnail
    if (imageUrl.includes("cloudinary.com")) {
      return imageUrl.replace("/upload/", "/upload/w_200,h_200,c_fill/");
    }
    return imageUrl;
  };

  return (
    <AdminLayout>
      <Box>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 600,
            mb: 1,
          }}
        >
          Image Settings
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            mb: 4,
          }}
        >
          Manage hero images and page images across your site
        </Typography>

        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "#fff",
          }}
        >
          <List sx={{ p: 0 }}>
            {IMAGE_SETTINGS.map((setting, index) => {
              const imageUrl = getImageUrl(setting.key);
              const previewUrl = getPreviewUrl(imageUrl);

              return (
                <ListItem
                  key={setting.key}
                  disablePadding
                  sx={{
                    borderBottom: index < IMAGE_SETTINGS.length - 1 ? "1px solid" : "none",
                    borderColor: "divider",
                  }}
                >
                  <ListItemButton
                    onClick={() => handleItemClick(setting.key)}
                    sx={{
                      py: 2,
                      px: 3,
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <Avatar
                      variant="rounded"
                      src={isLoading ? undefined : previewUrl}
                      sx={{
                        width: 80,
                        height: 80,
                        mr: 2,
                        bgcolor: "grey.200",
                        flexShrink: 0,
                      }}
                    >
                      {isLoading ? (
                        <Skeleton variant="rectangular" width="100%" height="100%" />
                      ) : previewUrl ? null : (
                        "No Image"
                      )}
                    </Avatar>
                    <ListItemText
                      primary={setting.label}
                      secondary={setting.description}
                      primaryTypographyProps={{
                        variant: "body1",
                        fontWeight: 500,
                      }}
                      secondaryTypographyProps={{
                        variant: "body2",
                        color: "text.secondary",
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Paper>
      </Box>

      {/* Modals */}
      <HeroImageSettingsModal
        open={heroImageModalOpen}
        onClose={handleModalClose}
      />

      <AboutHeroImageSettingsModal
        open={aboutHeroImageModalOpen}
        onClose={handleModalClose}
      />

      <AboutImageSettingsModal
        open={aboutImageModalOpen}
        onClose={handleModalClose}
      />

      <ContactHeroImageSettingsModal
        open={contactHeroImageModalOpen}
        onClose={handleModalClose}
      />
    </AdminLayout>
  );
};
